<template>
  <app-page-container class="permissions-page" :custom="1600" :min-height="0">
    <div v-if="$route.name === 'users-permissions-detail' && !editing" class="ui-page">
      <AppContentSkeleton v-if="loading" layout="block" label="Loading role" />
      <AppErrorState v-else-if="error" :message="error" icon-only @retry="retryError" />
    </div>
    <template v-else>
    <app-page-header
      design="new"
      compact
      :title="editing ? (role.id ? role.name : 'New role') : 'Roles & permissions'"
    >
    </app-page-header>
    <app-tabs v-if="!editing" design="new" refined weight="medium" :value="tab" :tabs="tabs" @input="switchTab" />
    <div class="ui-page">
    <p v-if="success" class="feedback feedback--success" role="status">{{ success }}</p>
    <template v-if="editing && role.id">
      <AppErrorState v-if="error" :message="error" icon-only @retry="retryError" />
      <AppTableText v-if="role.description" :text="role.description" />
      <div class="role-overview-grid">
        <app-card design="new" title="Module access" class="overview-card">
          <template #actions>
            <AppIconButton label="Edit module access" @click="startRoleEditing"><i class="pi pi-pencil" aria-hidden="true" /></AppIconButton>
          </template>
          <p v-if="!enabledSections.length" class="muted">No module access assigned.</p>
          <details v-for="section in enabledSections" :key="section.name" class="overview-section" open>
            <summary class="overview-section__summary">
              <span class="overview-section__name">{{ section.name }}</span>
              <span class="overview-count">{{ count(section.modules.length, 'module') }}</span>
              <span class="overview-chevron" aria-hidden="true" />
            </summary>
            <div class="overview-section__modules">
              <details v-for="module in section.modules" :key="module.key" class="overview-module">
                <summary class="overview-module__summary">
                  <span class="overview-module__name">{{ module.label }}</span>
                  <span class="overview-count">{{ count(module.actions.length + 1, 'permission') }}</span>
                  <span class="overview-chevron" aria-hidden="true" />
                </summary>
                <ul class="overview-actions">
                  <li>View lists and details</li>
                  <li v-for="action in module.actions" :key="action.key">{{ action.label }}</li>
                </ul>
              </details>
            </div>
          </details>
        </app-card>
        <app-card design="new" :title="`Assigned users (${role.member_count || 0})`" class="overview-card">
          <template #actions>
            <AppIconButton label="Edit assigned users" @click="openMemberEditor"><i class="pi pi-user-plus" aria-hidden="true" /></AppIconButton>
          </template>
          <p v-if="!(role.members || []).length" class="muted">No users assigned to this role.</p>
          <div v-for="member in role.members || []" :key="member.id" class="assigned-user">
            <img class="assigned-user__avatar" :src="pageUserAvatar(member)" alt="" @error="pageAvatarFallback" />
            <div class="assigned-user__info">
              <strong>{{ member.full_name || member.username }}</strong>
              <span class="assigned-user__groups">{{ groupNames(member) }}</span>
            </div>
            <span v-if="member.is_active === false" class="access-status access-status--inactive">Inactive</span>
          </div>
        </app-card>
      </div>
    </template>
    <template v-else-if="tab === 'roles'">
      <AppFilterToolbar>
        <template #create><app-button design="new" :disabled="loading" @click.native="openRole()">Create role</app-button></template>
        <template #primary><app-input v-model="roleSearch" design="new" type="search" placeholder="Search roles" aria-label="Search roles" /></template>
        <template v-if="roleSearch" #clear><AppClearFiltersButton design="new" icon-only @clear="roleSearch = ''" /></template>
      </AppFilterToolbar>
      <AppErrorState v-if="error" :message="error" icon-only @retry="retryError" />
      <AppContentSkeleton v-if="loading" label="Loading roles" :columns="roleColumns" />
      <app-card v-else-if="!error && filteredRoles.length" design="new" variant="table" header-density="compact">
        <app-data-table design="new" density="dense" :columns="roleColumns" :rows="filteredRoles" clickable @row-click="openRole($event.row)">
          <template #cell-name="{ row }"><AppTableText :text="row.name" /></template>
          <template #cell-description="{ row }"><AppTableText :text="row.description" /></template>
          <template #cell-access="{ row }"><AppTableText :text="moduleSummary(row.permissions)" /></template>
          <template #cell-member_count="{ row }"><AppTableText :text="count(row.member_count, 'user')" /></template>
        </app-data-table>
      </app-card>
      <app-no-results v-else-if="!error" design="new" :title="roleSearch ? 'No matching roles' : 'Start with a role'" text="" />
    </template>
    <template v-else-if="tab === 'users'">
      <AppFilterToolbar>
        <template #primary>
        <app-input v-model="accessSearch" design="new" type="search" placeholder="Search by name or email"
          aria-label="Search users" @input="onAccessSearch" /></template>
        <app-select v-model="accessGroupFilter" design="new" multiple :options="accessGroupOptions"
          :reduce="group => group.code" placeholder="All roles"
          aria-label="Filter by role" @input="onAccessFilterChange" />
        <template #selection><app-filters v-model="accessActiveFilter" design="new" refined weight="medium" name="accessActive"
          :filters="accessActiveFilters" aria-label="Account status" @change="onAccessFilterChange" /></template>
        <template #secondary-selection><app-filters v-model="accessSuperFilter" design="new" refined weight="medium" name="accessSuper"
          :filters="accessSuperFilters" aria-label="User type" @change="onAccessFilterChange" /></template>
        <template v-if="accessSearch || accessGroupFilter.length || accessActiveFilter !== 'true' || accessSuperFilter !== 'false'" #clear><AppClearFiltersButton design="new" icon-only @clear="clearAccessFilters" /></template>
      </AppFilterToolbar>
      <AppErrorState v-if="error" :message="error" icon-only @retry="retryError" />
      <app-card design="new" title="User access" class="user-access-card">
      <div class="user-access-grid">
        <section class="user-access-list" aria-label="Users">
          <AppContentSkeleton v-if="accessLoading" layout="block" label="Loading users" />
          <template v-else>
            <button v-for="person in users" :key="person.id" type="button" class="access-user-row"
              :class="{ 'access-user-row--selected': user && user.id === person.id }"
              :aria-pressed="Boolean(user && user.id === person.id)" @click="selectUser(person)">
              <img class="assigned-user__avatar" :src="pageUserAvatar(person)" alt="" @error="pageAvatarFallback" />
              <span class="access-user-row__info">
                <strong>{{ person.full_name || person.username }}</strong>
                <AppTableText :text="groupNames(person)" />
              </span>
              <span class="access-user-row__badges">
                <span v-if="!person.is_active" class="access-status access-status--inactive">Inactive</span>
                <span v-if="person.is_superuser" class="access-status">Super</span>
              </span>
            </button>
            <app-no-results v-if="!users.length && !error" design="new" title="No users found" text="" />
          </template>
          <app-pagination v-if="accessPage" design="new" placement="inline" label="User pages"
            :page="accessPage" :pages="accessPages" :count="accessCount" :visible-count="users.length"
            :page-size="20" :loading="accessLoading" @change="loadAccessUsers" />
        </section>
        <section class="user-access-detail" aria-label="Selected user access">
          <div v-if="!user" class="empty-state">
            <app-no-results design="new" title="Select a user" text="" />
          </div>
          <AppContentSkeleton v-else-if="loading" layout="block" label="Loading user access" />
          <template v-else-if="snapshot">
            <div class="user-access-detail__header">
              <img class="assigned-user__avatar" :src="pageUserAvatar(user)" alt="" @error="pageAvatarFallback" />
              <div class="assigned-user__info">
                <h2>{{ user.full_name || user.username }}</h2>
                <span class="muted">{{ user.email || 'No email' }} · {{ groupNames(user) }}</span>
                <span class="user-access-detail__status">{{ user.is_active ? 'Active' : 'Inactive' }} · {{ user.is_superuser ? 'Super' : 'Regular' }}</span>
              </div>
              <AppIconButton label="Edit assigned roles" @click="openUserRoleEditor"><i class="pi pi-pencil" aria-hidden="true" /></AppIconButton>
            </div>
            <p v-if="snapshot.is_general_manager" class="feedback">General Managers have full access. These assignments apply if this user is no longer a General Manager.</p>
            <p v-if="!user.is_active" class="feedback">This account is inactive. Assigned roles are stored, but they grant no access until the account is reactivated.</p>
            <h3>Assigned roles <span class="muted">{{ snapshot.roles.length }}</span></h3>
            <p v-if="!snapshot.roles.length" class="muted">No access roles assigned.</p>
            <div v-for="item in snapshot.roles" :key="item.id" class="user-access-role">
              <strong>{{ item.name }}</strong>
              <AppTableText class="muted" :text="moduleSummary(item.permissions)" />
            </div>
            <p class="muted user-access-detail__count">{{ count(snapshot.permissions.length, 'permission') }} from assigned roles</p>
          </template>
        </section>
      </div>
    </app-card>
    </template>
    <template v-else>
      <AppErrorState v-if="error" :message="error" icon-only @retry="retryError" />
      <AppContentSkeleton v-if="loading" layout="block" label="Loading access history" />
      <app-no-results v-else-if="!history.length && !error" design="new" title="No changes yet" text="" />
      <app-card v-else-if="!error" design="new" title="Access history">
      <article v-for="entry in history" :key="entry.id" class="history-entry">
        <div>
          <strong>{{ entry.additional_data.target_label || 'Access settings' }}</strong>
          <p class="muted">
            {{ historyTitle(entry) }} · {{ entry.actor_name || 'System' }} ·
            {{ new Date(entry.timestamp).toLocaleString() }}
          </p>
        </div>
        <details>
          <summary>View changes</summary>
          <div class="diff-grid">
            <section>
              <h3>Before</h3>
              <p>{{ historyText(entry.changes.permissions[0]) }}</p>
            </section>
            <section>
              <h3>After</h3>
              <p>{{ historyText(entry.changes.permissions[1]) }}</p>
            </section>
          </div>
        </details>
      </article>
      </app-card>
      <app-pagination v-if="!loading && !error && history.length"
        design="new"
        placement="embedded"
        :count="historyCount"
        :page="historyPage"
        :visible-count="history.length"
        :page-size="20"
        :loading="loading"
        @change="loadHistory"
      />
    </template>
    </div>
    </template>
    <modal
      name="role-editor"
      width="94%"
      :max-width="1400"
      height="auto"
      :adaptive="true"
      :scrollable="true"
      :click-to-close="false"
    >
      <article class="access-dialog role-editor-dialog">
        <app-modal-header design="new" :title="role.id ? 'Edit role' : 'Create role'" title-id="role-editor-title" @close="cancelRoleEditing" />
        <div class="role-editor-body">
          <p v-if="error" class="feedback feedback--error" role="alert">{{ error }}</p>
          <app-card design="new" title="Role details" class="content-card">
            <div class="details-grid">
              <app-form-group design="new" label="Role name" label-for="role-name" required
                ><app-input
                  id="role-name"
                  v-model="name"
                  design="new"
                  maxlength="160"
                  placeholder="For example, Altura coordinator"
                  :disabled="busy"
              /></app-form-group>
              <app-form-group design="new" label="Description" label-for="role-description"
                ><app-input
                  id="role-description"
                  v-model="description"
                  design="new"
                  maxlength="4000"
                  placeholder="Who needs this role?"
                  :disabled="busy"
              /></app-form-group>
            </div>
            <p class="muted">
              {{
                role.member_count
                  ? `Changes will apply to ${count(role.member_count, 'assigned user')}.`
                  : 'No users assigned yet. You can assign this role after saving.'
              }}
            </p>
          </app-card>
          <app-card design="new" title="Module access" class="content-card">
            <template #actions
              ><span class="muted module-count">{{ count(moduleCount(selected), 'module') }} enabled</span></template
            >
            <fieldset :disabled="busy">
              <permission-tree
                :key="role.id || 'new'"
                v-model="selected"
                :modules="catalog.modules"
                @confirm-remove="confirmRemoval"
              />
            </fieldset>
          </app-card>
          <div class="editor-bottom">
            <span class="muted">{{ dirty ? 'You have unsaved changes.' : 'All changes saved.' }}</span>
            <app-button v-if="role.id && !role.member_count" design="new" theme="error" :disabled="busy" @click.native="removeRole"
              >Delete role</app-button>
            <app-button design="new" theme="dark" :disabled="busy" @click.native="cancelRoleEditing">Cancel</app-button>
            <app-button ref="reviewButton" aria-label="Review changes" :aria-busy="busy" design="new"
              :disabled="busy || !dirty || !name.trim()" @click.native="review">Review changes</app-button>
          </div>
        </div>
      </article>
    </modal>
    <modal
      name="role-members-editor"
      width="92%"
      :max-width="1120"
      height="auto"
      :adaptive="true"
      :scrollable="true"
      :click-to-close="false"
    >
      <article class="access-dialog member-editor-dialog">
        <app-modal-header design="new" title="Assigned users" title-id="role-members-title" @close="closeMemberEditor" />
        <div class="dialog-body">
          <p v-if="error" class="feedback feedback--error" role="alert">{{ error }}</p>
          <div class="member-columns">
            <section class="member-column" aria-labelledby="available-users-title">
              <h3 id="available-users-title">Available users</h3>
              <label class="member-search-label" for="role-member-search">Search users</label>
              <app-input id="role-member-search" v-model="memberSearch" design="new" type="search"
                placeholder="Search by name or email" @input="onMemberSearch" />
              <label class="member-search-label member-role-label" for="role-member-groups">Role</label>
              <app-select id="role-member-groups" v-model="memberGroupFilter" design="new" multiple
                :options="memberGroupOptions" :reduce="group => group.code" :closeOnSelect="false"
                placeholder="All roles" @input="onMemberFilterChange" />
              <div ref="memberList" class="member-list" aria-live="polite">
                <div v-for="person in availableMemberResults" :key="person.id" class="assigned-user">
                  <img class="assigned-user__avatar" :src="userAvatar(person)" alt="" @error="avatarFallback" />
                  <div class="assigned-user__info">
                    <strong>{{ person.full_name || person.username }}</strong>
                    <span class="assigned-user__groups">{{ groupNames(person) }}</span>
                  </div>
                  <button type="button" class="member-transfer" :disabled="busy" :aria-label="`Select ${person.full_name || person.username} for role`" title="Add to selection" @click="stageMember(person, true)">→</button>
                </div>
                <p v-if="!memberLoading && !availableMemberResults.length && memberCount" class="member-empty muted">No available users on this page.</p>
                <p v-if="!memberLoading && !availableMemberResults.length && !memberCount" class="member-empty muted">No users found.</p>
                <p v-if="memberLoading" class="member-empty muted">Loading users…</p>
              </div>
              <app-pagination v-if="memberPage" design="new" placement="inline" label="Available user pages"
                :page="memberPage" :pages="memberPages" :count="memberCount" :visible-count="availableUsers.length"
                :page-size="20" :loading="memberLoading" @change="loadAvailableUsers" />
            </section>
            <section class="member-column" aria-labelledby="assigned-users-title">
              <h3 id="assigned-users-title">Selected users <span class="muted">{{ memberSelection.length }}</span></h3>
              <div class="member-list">
                <div v-for="member in memberSelection" :key="member.id" class="assigned-user member-selected-card"
                  :class="{ 'member-selected-card--new': membersAdded.some((person) => person.id === member.id) }">
                  <button type="button" class="member-transfer" :disabled="busy" :aria-label="`Remove ${member.full_name || member.username} from selection`" title="Remove from selection" @click="stageMember(member, false)">←</button>
                  <img class="assigned-user__avatar" :src="userAvatar(member)" alt="" @error="avatarFallback" />
                  <div class="assigned-user__info">
                    <strong>{{ member.full_name || member.username }}</strong>
                    <span class="assigned-user__groups">{{ groupNames(member) }}</span>
                  </div>
                  <span v-if="member.is_active === false" class="access-status access-status--inactive">Inactive</span>
                </div>
                <p v-if="!memberSelection.length" class="member-empty muted">No users selected.</p>
              </div>
            </section>
          </div>
        </div>
        <footer class="dialog-footer">
          <span v-if="memberChangesCount" class="muted member-change-count">{{ membersAdded.length }} to add · {{ membersRemoved.length }} to remove</span>
          <app-button design="new" theme="dark" :disabled="busy" @click.native="closeMemberEditor">{{ memberChangesCount ? 'Cancel' : 'Done' }}</app-button>
          <app-button design="new" :disabled="busy || !memberChangesCount" @click.native="reviewMemberChanges">Review changes</app-button>
        </footer>
      </article>
    </modal>
    <modal name="user-role-editor" width="92%" :max-width="1080" height="auto" :adaptive="true"
      :scrollable="true" :click-to-close="false">
      <article class="access-dialog user-role-editor-dialog">
        <app-modal-header design="new" :title="`Roles for ${user ? user.full_name || user.username : 'user'}`"
          title-id="user-role-editor-title" @close="closeUserRoleEditor" />
        <div class="dialog-body">
          <p v-if="error" class="feedback feedback--error" role="alert">{{ error }}</p>
          <div class="assignment-grid">
            <section>
              <h3>Assign roles <span class="muted">{{ selectedRoles.length }}</span></h3>
              <app-input v-model="roleSearch" design="new" type="search" placeholder="Find a role" aria-label="Find a role" />
              <div v-for="item in filteredRoles" :key="item.id" class="role-option">
                <app-checkbox design="new" :value="selectedRoles.includes(item.id)" :label="item.name"
                  @change="toggleRole(item.id, $event)" />
                <p>{{ moduleSummary(item.permissions) }}</p>
              </div>
              <p v-if="!filteredRoles.length" class="muted">No matching roles.</p>
            </section>
            <section class="access-summary">
              <h3>Resulting access</h3>
              <p v-if="!effectiveModules.length" class="muted">No module access from roles.</p>
              <div v-for="module in effectiveModules" :key="module.key" class="summary-module">
                <strong>{{ module.label }}</strong>
                <span class="muted">{{ count(module.permissions.filter((permission) => effectiveKeys.includes(permission.key)).length, 'permission') }}</span>
              </div>
            </section>
          </div>
        </div>
        <footer class="dialog-footer">
          <span class="muted member-change-count">{{ dirty ? 'Unsaved role assignments' : 'Assignments are up to date' }}</span>
          <app-button design="new" theme="dark" :disabled="busy" @click.native="closeUserRoleEditor">Cancel</app-button>
          <app-button ref="reviewButton" design="new" :aria-busy="busy" :disabled="!dirty || busy"
            @click.native="review">Review changes</app-button>
        </footer>
      </article>
    </modal>
    <modal
      ref="confirmModal"
      name="permission-confirm"
      width="92%"
      :max-width="640"
      height="auto"
      :adaptive="true"
      :scrollable="true"
      @before-close="beforeCloseDialog"
      @closed="afterCloseDialog"
      @opened="focusDialog"
    >
      <article class="access-dialog">
        <app-modal-header
          ref="dialogHeader"
          design="new"
          :title="dialog.title"
          title-id="access-dialog-title"
          @close="$modal.hide('permission-confirm')"
        />
        <div class="dialog-body">
          <p>{{ dialog.text }}</p>
          <ul v-if="dialog.items" class="removal-list"><li v-for="item in dialog.items" :key="item">{{ item }}</li></ul>
          <div v-if="dialog.memberChanges" class="diff-grid">
            <section>
              <h3>To add · {{ dialog.memberChanges.added.length }}</h3>
              <ul><li v-for="member in dialog.memberChanges.added" :key="member.id">{{ member.full_name || member.username }}</li></ul>
              <p v-if="!dialog.memberChanges.added.length" class="muted">None</p>
            </section>
            <section>
              <h3>To remove · {{ dialog.memberChanges.removed.length }}</h3>
              <ul><li v-for="member in dialog.memberChanges.removed" :key="member.id">{{ member.full_name || member.username }}{{ member.is_active === false ? ' (inactive)' : '' }}</li></ul>
              <p v-if="!dialog.memberChanges.removed.length" class="muted">None</p>
            </section>
          </div>
          <div v-if="preview && editing" class="diff-grid">
            <section>
              <h3>Added · {{ preview.added.length }}</h3>
              <ul>
                <li v-for="key in preview.added" :key="key">{{ labelFor(key) }}</li>
              </ul>
              <p v-if="!preview.added.length" class="muted">None</p>
            </section>
            <section>
              <h3>Removed · {{ preview.removed.length }}</h3>
              <ul>
                <li v-for="key in preview.removed" :key="key">{{ labelFor(key) }}</li>
              </ul>
              <p v-if="!preview.removed.length" class="muted">None</p>
            </section>
          </div>
          <template v-if="preview && !editing">
            <p class="role-review">{{ count(preview.added.length, 'permission') }} added · {{ count(preview.removed.length, 'permission') }} removed</p>
            <p v-if="user && !user.is_active" class="muted">This account is inactive; the permissions below cannot be used until it is reactivated.</p>
            <div class="diff-grid assignment-preview">
              <section>
                <h3>Current roles · {{ snapshot.roles.length }}</h3>
                <ul><li v-for="item in snapshot.roles" :key="item.id">{{ item.name }}</li></ul>
                <p v-if="!snapshot.roles.length" class="muted">None</p>
              </section>
              <section>
                <h3>After saving · {{ chosenRoles.length }}</h3>
                <ul><li v-for="item in chosenRoles" :key="item.id">{{ item.name }}</li></ul>
                <p v-if="!chosenRoles.length" class="muted">None</p>
              </section>
              <section>
                <h3>Current access · {{ preview.before.length }}</h3>
                <ul><li v-for="key in preview.before" :key="key">{{ labelFor(key) }}</li></ul>
                <p v-if="!preview.before.length" class="muted">None</p>
              </section>
              <section>
                <h3>After saving · {{ preview.permissions.length }}</h3>
                <ul><li v-for="key in preview.permissions" :key="key">{{ labelFor(key) }}</li></ul>
                <p v-if="!preview.permissions.length" class="muted">None</p>
              </section>
            </div>
          </template>
          <p v-if="dialogError" class="feedback feedback--error" role="alert">{{ dialogError }}</p>
        </div>
        <footer class="dialog-footer">
          <app-button
            design="new"
            theme="dark"
            :disabled="busy"
            @click.native="$modal.hide('permission-confirm')"
            >{{ preview || dialog.memberChanges ? 'Continue editing' : 'Cancel' }}</app-button
          ><app-button
            ref="confirmButton"
            design="new"
            :theme="dialog.destructive ? 'error' : 'default'"
            :disabled="busy"
            :aria-busy="busy"
            :aria-label="dialog.actionLabel"
            @click.native="runDialog"
            >{{ dialog.actionLabel }}</app-button
          >
        </footer>
      </article>
    </modal>
  </app-page-container>
</template>

<script>
import debounce from 'lodash/debounce'
import AppTabs from '@/components/AppTabs'
import AppContentSkeleton from '@/components/AppContentSkeleton.vue'
import AppErrorState from '@/components/AppErrorState.vue'
import AppIconButton from '@/components/AppIconButton.vue'
import AppTableText from '@/components/AppTableText.vue'
import avatarPlaceholder from '@/assets/img/user-avatar-placeholder.svg'
import PermissionTree from './PermissionTree'
import { definitions } from '@/utils/permissionSelection'
import {
  getPermissionCatalog,
  getPermissionRoles,
  getPermissionHistory,
  getUserPermissions,
  getUsers,
  getUsersGroups,
  postUserPermissionsPreview,
  postPermissionPreview,
  putUserPermissions,
  postPermissionRole,
  putPermissionRole,
  deletePermissionRole,
  postRoleMembersPreview,
  putRoleMembers
} from '@/http'
export default {
  components: { AppTabs, AppContentSkeleton, AppErrorState, AppIconButton, AppTableText, PermissionTree },
  data: () => ({
    tab: 'roles',
    tabs: [
      { value: 'roles', label: 'Roles' },
      { value: 'users', label: 'User access' },
      { value: 'history', label: 'History' }
    ],
    roleColumns: [
      { key: 'name', label: 'Role', width: '24%' },
      { key: 'description', label: 'Description', width: '30%' },
      { key: 'access', label: 'Module access', width: '28%' },
      { key: 'member_count', label: 'Assigned users', width: '18%' }
    ],
    catalog: { modules: [] },
    roles: [],
    users: [],
    user: null,
    snapshot: null,
    selectedRoles: [],
    accessSearch: '',
    accessGroupFilter: [],
    accessGroupOptions: [],
    accessActiveFilter: 'true',
    accessActiveFilters: [
      { value: 'true', label: 'Active' },
      { value: 'false', label: 'Not active' }
    ],
    accessSuperFilter: 'false',
    accessSuperFilters: [
      { value: 'false', label: 'Regular' },
      { value: 'true', label: 'Super' }
    ],
    accessPage: 0,
    accessPages: 0,
    accessCount: 0,
    accessLoading: false,
    roleSearch: '',
    editing: false,
    roleEditing: false,
    memberEditorOpen: false,
    memberSelection: [],
    memberSearch: '',
    memberGroupFilter: [],
    memberGroupOptions: [],
    availableUsers: [],
    memberPage: 0,
    memberPages: 0,
    memberCount: 0,
    memberLoading: false,
    memberSearchSequence: 0,
    role: {},
    name: '',
    description: '',
    selected: [],
    loading: true,
    busy: false,
    error: '',
    success: '',
    history: [],
    historyPage: 1,
    retryHistoryPage: 1,
    retryAccessPage: 1,
    failedRequest: '',
    historyCount: 0,
    preview: null,
    dialog: {},
    dialogError: '',
    dialogOpen: false,
    returnFocus: null,
    searchSequence: 0
  }),
  computed: {
    enabledSections() {
      const sections = []
      this.catalog.modules.forEach((module) => {
        if (!module.permissions.some((permission) => this.selected.includes(permission.key))) return
        let section = sections.find((item) => item.name === module.section)
        if (!section) {
          section = { name: module.section, modules: [] }
          sections.push(section)
        }
        section.modules.push({
          ...module,
          actions: module.permissions.filter((permission) => permission.action !== 'list' && this.selected.includes(permission.key))
        })
      })
      return sections
    },
    availableMemberResults() {
      const selected = new Set(this.memberSelection.map((member) => member.id))
      const removed = this.membersRemoved.filter((member) => {
        const query = (this.memberSearch || '').toLowerCase()
        return member.is_active !== false &&
          (!query || `${member.full_name} ${member.email || ''}`.toLowerCase().includes(query)) &&
          (!this.memberGroupFilter.length || (member.groups || []).some((group) => this.memberGroupFilter.includes(group.code)))
      })
      return [...removed, ...this.availableUsers].filter((user, index, users) => {
        return !selected.has(user.id) && users.findIndex((item) => item.id === user.id) === index
      })
    },
    membersAdded() {
      const original = new Set((this.role.members || []).map((member) => member.id))
      return this.memberSelection.filter((member) => !original.has(member.id))
    },
    membersRemoved() {
      const selected = new Set(this.memberSelection.map((member) => member.id))
      return (this.role.members || []).filter((member) => !selected.has(member.id))
    },
    memberChangesCount() {
      return this.membersAdded.length + this.membersRemoved.length
    },
    filteredRoles() {
      const q = (this.roleSearch || '').toLowerCase()
      return this.roles.filter((r) => `${r.name} ${r.description}`.toLowerCase().includes(q))
    },
    chosenRoles() {
      return this.roles.filter((r) => this.selectedRoles.includes(r.id))
    },
    effectiveKeys() {
      return [...new Set(this.chosenRoles.reduce((all, r) => all.concat(r.permissions), []))]
    },
    effectiveModules() {
      return this.catalog.modules.filter((m) => m.permissions.some((p) => this.effectiveKeys.includes(p.key)))
    },
    dirty() {
      if (this.memberEditorOpen && this.memberChangesCount) return true
      if (this.editing && !this.roleEditing) return false
      if (this.editing) return (
        !this.role.id ||
          this.name !== this.role.name ||
          this.description !== this.role.description ||
          JSON.stringify([...this.selected].sort()) !==
            JSON.stringify([...(this.role.permissions || [])].sort())
      )
      return Boolean(
        this.snapshot &&
          JSON.stringify([...this.selectedRoles].sort()) !==
            JSON.stringify([...this.snapshot.role_ids].sort())
      )
    }
  },
  watch: {
    busy(value) {
      if (this.$refs.reviewButton) this.$refs.reviewButton.preload = value && !this.dialogOpen
    },
    $route(to) {
      this.syncRoute(to)
    }
  },
  created() {
    this.searchAccessUsers = debounce(() => this.loadAccessUsers(1), 250)
    this.searchAvailableUsers = debounce(() => this.loadAvailableUsers(1), 250)
    this.initialize()
  },
  mounted() {
    window.addEventListener('beforeunload', this.beforeUnload)
  },
  beforeDestroy() {
    this.searchAccessUsers.cancel()
    this.searchAvailableUsers.cancel()
    window.removeEventListener('beforeunload', this.beforeUnload)
  },
  beforeRouteLeave(to, from, next) {
    if (['login', 'access-denied'].includes(to.name) || !this.dirty) next()
    else {
      this.askDiscard(() => next())
      this.dialog.cancel = () => next(false)
    }
  },
  beforeRouteUpdate(to, from, next) {
    if (!this.dirty) next()
    else {
      this.askDiscard(() => next())
      this.dialog.cancel = () => next(false)
    }
  },
  methods: {
    count(n, word) {
      return `${n} ${word}${n === 1 ? '' : 's'}`
    },
    moduleCount(keys) {
      return this.catalog.modules.filter((m) => m.permissions.some((p) => keys.includes(p.key))).length
    },
    moduleSummary(keys) {
      return (
        this.catalog.modules
          .filter((m) => m.permissions.some((p) => keys.includes(p.key)))
          .map((m) => m.label)
          .join(', ') || 'No module access'
      )
    },
    labelFor(key) {
      const p = definitions(this.catalog.modules).find((p) => p.key === key)
      const m = this.catalog.modules.find((m) => m.permissions.some((p) => p.key === key))
      return p ? `${m.label} · ${p.label}` : key
    },
    beforeUnload(event) {
      if (this.dirty) {
        event.preventDefault()
        event.returnValue = ''
      }
    },
    async initialize() {
      this.loading = true
      this.error = ''
      try {
        const [catalog, roles] = await Promise.all([getPermissionCatalog(), getPermissionRoles()])
        this.catalog = catalog.data
        this.roles = roles.data
        this.syncRoute(this.$route)
      } catch (e) {
        this.error = e.message
      } finally {
        this.loading = false
      }
    },
    clearAccessFilters() {
      this.searchAccessUsers.cancel()
      this.accessSearch = ''
      this.accessGroupFilter = []
      this.accessActiveFilter = 'true'
      this.accessSuperFilter = 'false'
      this.loadAccessUsers(1)
    },
    onAccessSearch() {
      this.searchAccessUsers()
    },
    onAccessFilterChange() {
      this.searchAccessUsers.cancel()
      this.loadAccessUsers(1)
    },
    async loadAccessGroups() {
      try {
        this.accessGroupOptions = (await getUsersGroups()).data
      } catch (e) {
        this.failedRequest = 'groups'
        this.error = e.message
      }
    },
    async loadAccessUsers(page) {
      this.retryAccessPage = page
      if (this.failedRequest === 'users') { this.failedRequest = ''; this.error = '' }
      const sequence = ++this.searchSequence
      this.accessLoading = true
      try {
        const response = await getUsers({
          search: this.accessSearch,
          groups: this.accessGroupFilter.join(','),
          is_active: this.accessActiveFilter === 'true',
          is_superuser: this.accessSuperFilter === 'true',
          page,
          page_size: 20
        })
        if (sequence !== this.searchSequence) return
        this.users = response.data.results
        if (this.user) {
          const visibleUser = this.users.find((person) => person.id === this.user.id)
          if (visibleUser) this.user = visibleUser
          else {
            this.user = null
            this.snapshot = null
            this.selectedRoles = []
          }
        }
        this.accessPage = page
        this.accessPages = response.data.pages
        this.accessCount = response.data.count
      } catch (e) {
        if (sequence === this.searchSequence) { this.failedRequest = 'users'; this.error = e.message }
      } finally {
        if (sequence === this.searchSequence) this.accessLoading = false
      }
    },
    changeContext(action) {
      if (this.busy) return
      if (this.dirty) this.askDiscard(action)
      else action()
    },
    askDiscard(action) {
      this.openDialog({
        title: 'Discard unsaved changes?',
        text: 'Your saved access settings will stay unchanged.',
        actionLabel: 'Discard changes',
        destructive: true,
        action
      })
    },
    switchTab(tab) {
      if (tab === this.tab) return
      this.changeContext(() => {
        this.editing = false
        this.snapshot = null
        this.user = null
        this.$modal.hide('user-role-editor')
        this.roleSearch = ''
        this.success = ''
        this.error = ''
        this.failedRequest = ''
        this.tab = tab
        if (tab === 'history') this.loadHistory(1)
        if (tab === 'users') {
          this.loadAccessGroups()
          this.loadAccessUsers(1)
        }
      })
    },
    openRole(role = {}) {
      if (role.id) this.$router.push({ name: 'users-permissions-detail', params: { id: role.id } })
      else this.$router.push({ name: 'users-permissions-new' })
    },
    syncRoute(route) {
      if (route.name === 'users-permissions-new') this.applyRole({})
      else if (route.name === 'users-permissions-detail') {
        const role = this.roles.find((item) => String(item.id) === String(route.params.id))
        if (role) this.applyRole(role)
        else {
          this.editing = false
          this.error = 'Role not found. Return to the roles list.'
        }
      } else {
        this.editing = false
        this.roleEditing = false
        this.memberEditorOpen = false
        this.$modal.hide('role-editor')
        this.$modal.hide('role-members-editor')
        this.$modal.hide('user-role-editor')
      }
    },
    applyRole(role) {
      this.role = role
      this.name = role.name || ''
      this.description = role.description || ''
      this.selected = [...(role.permissions || [])]
      this.editing = true
      this.roleEditing = !role.id
      this.memberEditorOpen = false
      this.memberSelection = [...(role.members || [])]
      this.memberSearch = ''
      this.error = ''
      this.success = ''
      if (this.roleEditing) this.$nextTick(() => this.$modal.show('role-editor'))
      else this.$modal.hide('role-editor')
      window.scrollTo(0, 0)
    },
    leaveEditor() {
      this.$router.push({ name: 'users-permissions' }).catch(() => null)
    },
    startRoleEditing() {
      this.name = this.role.name
      this.description = this.role.description || ''
      this.selected = [...this.role.permissions]
      this.roleEditing = true
      this.$nextTick(() => this.$modal.show('role-editor'))
    },
    cancelRoleEditing() {
      if (!this.role.id) {
        this.$modal.hide('role-editor')
        return this.leaveEditor()
      }
      const reset = () => {
        this.name = this.role.name
        this.description = this.role.description || ''
        this.selected = [...this.role.permissions]
        this.roleEditing = false
        this.$modal.hide('role-editor')
      }
      if (this.dirty) this.askDiscard(reset)
      else reset()
    },
    openMemberEditor() {
      this.memberSelection = [...(this.role.members || [])]
      this.memberEditorOpen = true
      this.memberSearch = ''
      this.memberGroupFilter = []
      this.availableUsers = []
      this.memberPage = 0
      this.memberPages = 0
      this.memberCount = 0
      this.loadAvailableUsers(1)
      if (!this.memberGroupOptions.length) this.loadMemberGroups()
      this.$modal.show('role-members-editor')
    },
    closeMemberEditor() {
      if (this.memberChangesCount) this.askDiscard(this.resetMemberEditor)
      else this.resetMemberEditor()
    },
    resetMemberEditor() {
      this.memberSelection = [...(this.role.members || [])]
      this.memberEditorOpen = false
      this.memberSearch = ''
      this.searchAvailableUsers.cancel()
      this.memberSearchSequence += 1
      this.$modal.hide('role-members-editor')
    },
    onMemberSearch(value) {
      this.memberSearch = value
      this.resetMemberResults()
      this.searchAvailableUsers()
    },
    onMemberFilterChange() {
      this.searchAvailableUsers.cancel()
      this.resetMemberResults()
      this.loadAvailableUsers(1)
    },
    resetMemberResults() {
      this.memberSearchSequence += 1
      this.availableUsers = []
      this.memberPage = 0
      this.memberPages = 0
      this.memberCount = 0
      this.memberLoading = true
    },
    async loadMemberGroups() {
      try {
        this.memberGroupOptions = (await getUsersGroups()).data
      } catch (e) {
        this.error = e.message
      }
    },
    async loadAvailableUsers(page) {
      const sequence = ++this.memberSearchSequence
      this.memberLoading = true
      try {
        const response = await getUsers({ search: this.memberSearch, groups: this.memberGroupFilter.join(','), is_active: true, exclude_access_role: this.role.id, page, page_size: 20 })
        if (sequence !== this.memberSearchSequence) return
        this.availableUsers = response.data.results
        this.memberPage = page
        this.memberPages = response.data.pages
        this.memberCount = response.data.count
        this.$nextTick(() => { if (this.$refs.memberList) this.$refs.memberList.scrollTop = 0 })
      } catch (e) {
        if (sequence === this.memberSearchSequence) this.error = e.message
      } finally {
        if (sequence === this.memberSearchSequence) this.memberLoading = false
      }
    },
    pageUserAvatar(user) {
      return user.crop?.crop_url || avatarPlaceholder
    },
    pageAvatarFallback(event) {
      const fallback = avatarPlaceholder
      if (event.target.getAttribute('src') !== fallback) event.target.src = fallback
    },
    userAvatar(user) {
      return user.crop?.crop_url || require('@/assets/img/temp/avatar-slug.jpg')
    },
    avatarFallback(event) {
      const fallback = require('@/assets/img/temp/avatar-slug.jpg')
      if (event.target.getAttribute('src') !== fallback) event.target.src = fallback
    },
    groupNames(user) {
      return (user.groups || []).map((group) => group.name).join(', ') || 'No business roles'
    },
    stageMember(member, add) {
      if (this.busy) return
      if (add && this.membersAdded.length >= 100) {
        this.error = 'Update up to 100 users at a time.'
        return
      }
      if (!add && this.membersRemoved.length >= 100) {
        this.error = 'Update up to 100 users at a time.'
        return
      }
      this.error = ''
      this.memberSelection = add ? [...this.memberSelection, member] : this.memberSelection.filter((person) => person.id !== member.id)
    },
    memberPayload() {
      return {
        add_ids: this.membersAdded.map((member) => member.id),
        remove_ids: this.membersRemoved.map((member) => member.id),
        grants_hash: this.role.grants_hash,
        members_hash: this.role.members_hash,
        catalog_version: this.catalog.catalog_version
      }
    },
    async reviewMemberChanges() {
      if (this.busy || !this.memberChangesCount) return
      this.busy = true
      this.error = ''
      try {
        const payload = this.memberPayload()
        const response = await postRoleMembersPreview(this.role.id, payload)
        this.openDialog({
          title: 'Review user assignments',
          text: `“${this.role.name}” will have ${this.count(response.data.final_count, 'assigned user')} after saving.`,
          memberChanges: response.data,
          actionLabel: 'Save assignments',
          action: async () => {
            const saved = (await putRoleMembers(this.role.id, payload)).data
            this.roles = this.roles.map((role) => role.id === saved.id ? saved : role)
            this.role = saved
            this.selected = [...this.role.permissions]
            this.resetMemberEditor()
            this.success = 'User assignments saved.'
            await this.$store.dispatch('permissions/refreshProfile', true)
          }
        })
      } catch (e) {
        this.error = e.message
      } finally {
        this.busy = false
      }
    },
    selectUser(user) {
      this.changeContext(() => this.loadUser(user))
    },
    openUserRoleEditor() {
      if (!this.snapshot) return
      this.selectedRoles = [...this.snapshot.role_ids]
      this.roleSearch = ''
      this.$modal.show('user-role-editor')
    },
    closeUserRoleEditor() {
      const close = () => {
        this.selectedRoles = [...this.snapshot.role_ids]
        this.$modal.hide('user-role-editor')
      }
      if (this.dirty) this.askDiscard(close)
      else close()
    },
    async loadUser(user) {
      this.user = user
      this.snapshot = null
      this.success = ''
      this.error = ''
      if (!user) return
      this.loading = true
      try {
        const [snapshot, roles, catalog] = await Promise.all([
          getUserPermissions(user.id),
          getPermissionRoles(),
          getPermissionCatalog()
        ])
        if (!this.user || this.user.id !== user.id) return
        this.snapshot = snapshot.data
        this.selectedRoles = [...snapshot.data.role_ids]
        this.roles = roles.data
        this.catalog = catalog.data
      } catch (e) {
        this.error = e.message
      } finally {
        this.loading = false
      }
    },
    toggleRole(id, on) {
      this.selectedRoles = on ? [...new Set([...this.selectedRoles, id])] : this.selectedRoles.filter((value) => value !== id)
      this.success = ''
    },
    discardAssignments() {
      this.askDiscard(() => {
        this.selectedRoles = [...this.snapshot.role_ids]
      })
    },
    payload() {
      return this.editing ? {
        name: this.name.trim(),
        description: this.description || '',
        permissions: this.selected,
        catalog_version: this.catalog.catalog_version,
        grants_hash: this.role.grants_hash
      } : {
        role_ids: this.selectedRoles,
        role_versions: Object.fromEntries(this.chosenRoles.map((r) => [r.id, r.grants_hash])),
        grants_hash: this.snapshot.grants_hash,
        catalog_version: this.catalog.catalog_version
      }
    },
    async review() {
      this.busy = true
      this.error = ''
      try {
        const response = this.editing ? await postPermissionPreview(this.payload()) : await postUserPermissionsPreview(this.user.id, this.payload())
        const before = this.editing ? this.role.permissions || [] : this.snapshot.permissions
        const result = response.data
        this.openDialog({
          title: this.editing ? 'Review role changes' : 'Review role assignments',
          text: this.editing ? this.role.member_count ? `Saving will update access for ${this.count(this.role.member_count, 'assigned user')}.` : `Save “${this.name.trim()}” with the access below.` : `Update roles for ${this.user.full_name}. Access comes from all selected roles.`,
          actionLabel: this.editing ? 'Save role' : 'Save assignments',
          action: this.save
        })
        this.preview = {
          ...result,
          before,
          added: result.permissions.filter((k) => !before.includes(k)),
          removed: before.filter((k) => !result.permissions.includes(k))
        }
      } catch (e) {
        this.error = e.message
      } finally {
        this.busy = false
      }
    },
    async save() {
      if (this.editing) {
        const wasNew = !this.role.id
        const response = this.role.id ? await putPermissionRole(this.role.id, this.payload()) : await postPermissionRole(this.payload())
        this.role = response.data
        this.name = response.data.name
        this.description = response.data.description
        this.selected = [...response.data.permissions]
        this.roles = (await getPermissionRoles()).data
        if (wasNew) await this.$router.replace({ name: 'users-permissions-detail', params: { id: response.data.id } })
        this.roleEditing = false
        this.$modal.hide('role-editor')
        this.success = 'Role saved. Assigned users now have the updated access.'
      } else {
        const response = await putUserPermissions(this.user.id, this.payload())
        this.snapshot = response.data
        this.selectedRoles = [...response.data.role_ids]
        this.roles = (await getPermissionRoles()).data
        this.$modal.hide('user-role-editor')
        this.success = 'Role assignments saved.'
      }
      await this.$store.dispatch('permissions/refreshProfile', true)
    },
    confirmRemoval({ removed, apply }) {
      this.openDialog({
        title: 'Remove this access?',
        items: removed.map(this.labelFor),
        text: `This removes ${this.count(
          removed.length,
          'permission'
        )}, including actions that depend on it. Changes take effect only after you save the role.`,
        actionLabel: 'Remove access',
        destructive: true,
        action: apply
      })
    },
    removeRole() {
      if (this.role.member_count) {
        this.openDialog({
          title: 'This role is assigned to users',
          text: `Remove this role from ${this.count(
            this.role.member_count,
            'user'
          )} in User access before deleting it.`,
          actionLabel: 'Got it',
          action: () => {}
        })
        return
      }
      this.openDialog({
        title: 'Delete this role?',
        text: `“${this.role.name}” will be deleted. Its change history will remain available.`,
        actionLabel: 'Delete role',
        destructive: true,
        action: async () => {
          await deletePermissionRole(this.role.id, { grants_hash: this.role.grants_hash })
          this.roles = (await getPermissionRoles()).data
          this.editing = false
          await this.$router.replace({ name: 'users-permissions' })
          this.success = 'Role deleted.'
        }
      })
    },
    openDialog(dialog) {
      this.returnFocus = document.activeElement
      this.dialog = dialog
      this.preview = null
      this.dialogError = ''
      this.dialogOpen = true
      this.$modal.show('permission-confirm')
    },
    focusDialog() {
      this.$refs.confirmModal.$refs.modal.setAttribute('aria-labelledby', 'access-dialog-title')
      this.$refs.dialogHeader.focusClose()
    },
    beforeCloseDialog(event) {
      if (this.busy) event.cancel()
    },
    afterCloseDialog() {
      this.dialogOpen = false
      this.preview = null
      if (this.dialog.cancel) this.dialog.cancel()
      if (this.returnFocus?.isConnected) this.returnFocus.focus()
    },
    async runDialog() {
      if (this.busy) return
      this.busy = true
      this.$refs.confirmButton.preload = true
      try {
        await this.dialog.action()
        this.dialog.cancel = null
        this.busy = false
        this.$modal.hide('permission-confirm')
      } catch (e) {
        this.dialogError = e.message
        this.error = e.message
      } finally {
        this.busy = false
        this.$refs.confirmButton.preload = false
      }
    },
    reload() {
      this.changeContext(async () => {
        await this.initialize()
        if (this.tab === 'users') {
          await Promise.all([this.loadAccessGroups(), this.loadAccessUsers(1)])
        }
        if (this.user) await this.loadUser(this.user)
        else if (this.tab === 'history') await this.loadHistory(1)
      })
    },
    retryError() {
      if (this.failedRequest === 'history') this.loadHistory(this.retryHistoryPage)
      else if (this.failedRequest === 'users') this.loadAccessUsers(this.retryAccessPage)
      else if (this.failedRequest === 'groups') {
        this.error = ''
        this.failedRequest = ''
        this.loadAccessGroups()
      } else this.reload()
    },
    async loadHistory(page) {
      this.retryHistoryPage = page
      this.failedRequest = ''
      this.error = ''
      this.loading = true
      try {
        const r = await getPermissionHistory({ page, page_size: 20 })
        this.history = r.data.results
        this.historyCount = r.data.count
        this.historyPage = page
      } catch (e) {
        this.failedRequest = 'history'
        this.error = e.message
      } finally {
        this.loading = false
      }
    },
    historyTitle(entry) {
      return `${entry.additional_data.target_type === 'user' ? 'Role assignments' : 'Role'} ${
        entry.action === 0 ? 'created' : entry.action === 2 ? 'deleted' : 'updated'
      }`
    },
    historyText(value) {
      if (!value) return 'None'
      if (Array.isArray(value)) return value.map(this.labelFor).join(', ') || 'No access'
      if (value.roles) return value.roles.map((r) => r.name).join(', ') || 'No roles'
      return [value.name, ...(value.permissions || []).map(this.labelFor)].join(' · ')
    }
  }
}
</script>

<style scoped lang="scss">
.permissions-page {
  overflow-wrap: anywhere;
  color: $ui-text;
  font-family: $ui-font-body;
  font-size: $ui-font-size-body;
  line-height: 1.45;
}
.content-card {
  margin-bottom: $ui-space-16;
}
.role-editor-body { padding: $ui-space-20; }
.role-editor-body .content-card:last-of-type { margin-bottom: 0; }
.role-editor-body .editor-bottom { margin-top: $ui-space-16; }
.role-editor-dialog,
.member-editor-dialog { background: $ui-surface; }
.details-grid {
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: $ui-space-24;
}
fieldset {
  min-width: 0;
  border: 0;
  margin: 0;
  padding: 0;
}
.muted,
.role-description {
  color: $ui-muted;
}
.text-link {
  color: $ui-blue;
  font: inherit;
  text-align: left;
}
.role-title {
  font-weight: 600;
  display: block;
  max-width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.role-cell-ellipsis {
  display: block;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.roles-table {
  :deep(table) { table-layout: fixed; }
  :deep(th) { white-space: nowrap; }
  :deep(td) { min-width: 0; }
}
.empty-state {
  text-align: center;
  padding: $ui-space-48 $ui-space-16;
  color: $ui-muted;
}
h2,
h3 {
  font: 600 $ui-font-size-section-title $ui-font-heading;
  color: $ui-ink;
  margin-bottom: $ui-space-8;
}
.feedback {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: $ui-space-12;
  padding: $ui-space-16;
  border-radius: $ui-control-radius;
  background: $ui-blue-soft;
  margin-bottom: $ui-space-16;
}
.feedback--error {
  color: $ui-red;
  background: $ui-red-soft;
}
.feedback--success {
  color: $ui-green;
  background: $ui-green-soft;
}
.role-overview-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(0, .85fr);
  align-items: start;
  gap: $ui-space-16;
}
.card-icon-button {
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  border: 1px solid $ui-line;
  border-radius: $ui-control-radius;
  color: $ui-blue;
  background: $ui-surface;
}
.card-icon-button:hover { background: $ui-blue-soft; }
.card-icon-button svg { width: 20px; height: 20px; }
.overview-section {
  margin-top: $ui-space-8;
  border: 1px solid $ui-line;
  border-radius: $ui-control-radius;
  overflow: hidden;
}
.overview-section__summary,
.overview-module__summary {
  display: flex;
  align-items: center;
  min-height: 42px;
  gap: $ui-space-8;
  padding: $ui-space-8 $ui-space-12;
  list-style: none;
  cursor: pointer;
}
.overview-section__summary::-webkit-details-marker,
.overview-module__summary::-webkit-details-marker { display: none; }
.overview-section__summary {
  color: $ui-ink;
  background: $ui-surface-soft;
  font-weight: 600;
}
.overview-section[open] > .overview-section__summary {
  background: $ui-blue-soft;
}
.overview-section__name,
.overview-module__name { flex: 1; min-width: 0; }
.overview-count { color: $ui-muted; font-weight: 400; white-space: nowrap; }
.overview-chevron {
  width: 8px;
  height: 8px;
  margin: 0 $ui-space-4;
  border-right: 1.5px solid currentColor;
  border-bottom: 1.5px solid currentColor;
  transform: rotate(45deg);
  transition: transform .15s ease;
}
details[open] > summary .overview-chevron { transform: rotate(225deg); }
.overview-section__modules {
  padding-left: $ui-space-12;
  border-left: 3px solid $ui-blue;
}
.overview-module {
  margin-left: $ui-space-8;
}
.overview-module + .overview-module { border-top: 1px solid $ui-line; }
.overview-module__summary {
  min-height: 34px;
  margin: $ui-space-4 $ui-space-8;
  padding: $ui-space-4 $ui-space-12;
  border-radius: $ui-control-radius;
  color: $ui-text;
}
.overview-module__summary:hover,
.overview-module[open] > .overview-module__summary { background: $ui-surface-soft; }
.overview-module__name { font-weight: 600; }
.overview-actions {
  margin: 0;
  padding: 0 $ui-space-12 $ui-space-8;
  list-style: none;
}
.overview-actions li {
  display: flex;
  align-items: baseline;
  gap: $ui-space-8;
  padding: $ui-space-4 $ui-space-8;
  color: $ui-text;
  line-height: 1.4;
}
.overview-actions li + li { border-top: 1px solid $ui-line; }
.overview-actions li::before { content: '✓'; color: $ui-green; font-weight: 600; }
.overview-actions li:first-child { color: $ui-green; }
.assigned-user {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: $ui-space-12;
  padding: $ui-space-12 0;
  border-top: 1px solid $ui-line;
}
.assigned-user__avatar {
  flex: 0 0 42px;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  object-fit: cover;
}
.assigned-user__info {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  line-height: 1.35;
}
.assigned-user__info strong { color: $ui-ink; overflow-wrap: anywhere; }
.assigned-user__info .muted { font-size: $ui-font-size-metadata; }
.assigned-user__groups { color: $ui-muted; overflow-wrap: anywhere; }
.member-columns {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: $ui-space-24;
}
.member-column { min-width: 0; }
.member-column + .member-column { border-left: 1px solid $ui-line; padding-left: $ui-space-24; }
.member-column h3 { margin-bottom: $ui-space-16; }
.member-column h3 .muted { margin-left: $ui-space-4; font-weight: 400; }
.member-search-label { display: block; margin-bottom: $ui-space-8; font-weight: 600; }
.member-role-label { margin-top: $ui-space-16; }
.member-list { max-height: 52vh; min-height: 180px; overflow-y: auto; margin-top: $ui-space-16; }
.member-column :deep(.app-pagination) { justify-content: center; }
.member-column :deep(.app-pagination__records) { display: none; }
.member-empty { padding: $ui-space-16 0; }
.member-transfer {
  display: grid;
  place-items: center;
  flex: 0 0 34px;
  width: 34px;
  height: 34px;
  border: 1px solid $ui-line;
  border-radius: $ui-control-radius;
  color: $ui-blue;
  background: $ui-surface;
  font-size: 22px;
  line-height: 1;
}
.member-transfer:hover { background: $ui-blue-soft; }
.member-transfer:disabled { opacity: .5; }
.member-selected-card {
  margin-bottom: $ui-space-8;
  padding: $ui-space-12;
  border: 1px solid $ui-line;
  border-radius: $ui-control-radius;
  background: $ui-surface;
}
.member-selected-card--new {
  border-color: $ui-blue;
  background: $ui-blue-soft;
  box-shadow: inset 3px 0 0 $ui-blue;
}
.member-selected-card--new .member-transfer { background: $ui-surface; }
.member-change-count { margin-right: auto; }
.overview-card { min-height: 180px; }
.editor-bottom {
  justify-content: flex-end;
}
.user-access-filters {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: $ui-space-12;
  padding-bottom: $ui-space-20;
}
.user-access-filters > :first-child { flex: 1 1 240px; }
.user-access-filters > :nth-child(2) { flex: 1 1 220px; min-width: 220px; }
.user-access-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  border-top: 1px solid $ui-line;
}
.user-access-list { min-width: 0; padding-right: $ui-space-20; }
.user-access-detail { min-width: 0; padding: $ui-space-20 0 $ui-space-20 $ui-space-24; border-left: 1px solid $ui-line; }
.user-access-message { padding: $ui-space-20; }
.access-user-row {
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
  gap: $ui-space-12;
  padding: $ui-space-8 $ui-space-12;
  border: 1px solid transparent;
  border-bottom-color: $ui-line;
  border-radius: $ui-control-radius;
  color: $ui-text;
  text-align: left;
  background: $ui-surface;
}
.access-user-row:hover { background: $ui-surface-soft; }
.access-user-row--selected { border-color: $ui-blue; background: $ui-blue-soft; }
.access-user-row__info { display: flex; flex: 1; flex-direction: column; min-width: 0; line-height: 1.35; }
.access-user-row__info strong { overflow: hidden; color: $ui-ink; text-overflow: ellipsis; white-space: nowrap; }
.access-user-row__info > span { overflow: hidden; color: $ui-muted; text-overflow: ellipsis; white-space: nowrap; }
.access-user-row__badges { display: flex; flex: 0 0 auto; flex-direction: column; align-items: flex-end; gap: $ui-space-4; }
.access-status { padding: 2px $ui-space-8; border-radius: $ui-control-radius; color: $ui-blue; background: $ui-blue-soft; font-size: $ui-font-size-metadata; }
.access-status--inactive { color: $ui-red; background: $ui-red-soft; }
.user-access-detail__header { display: flex; align-items: center; gap: $ui-space-12; margin-bottom: $ui-space-20; }
.user-access-detail__header .assigned-user__info { gap: $ui-space-4; }
.user-access-detail__header h2 { margin: 0; }
.user-access-detail__status { color: $ui-muted; font-size: $ui-font-size-metadata; }
.user-access-detail h3 { margin-bottom: $ui-space-12; }
.user-access-role { display: flex; flex-direction: column; gap: $ui-space-4; padding: $ui-space-12 0; border-top: 1px solid $ui-line; }
.user-access-role strong { color: $ui-ink; }
.user-access-role span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.user-access-detail__count { margin-top: $ui-space-16; }
.user-role-editor-dialog { background: $ui-surface; }
.user-role-editor-dialog .dialog-body { max-height: 68vh; overflow-y: auto; }
.user-role-editor-dialog .assignment-grid { margin-top: 0; }
.assignment-preview ul { max-height: 220px; overflow-y: auto; }
.assignment-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: $ui-space-32;
  margin-top: $ui-space-24;
}
.section-copy {
  margin-bottom: $ui-space-16;
}
.role-option {
  padding: $ui-space-16 0;
  border-bottom: 1px solid $ui-line;
}
.role-option p {
  color: $ui-muted;
  margin: $ui-space-4 0 0 $ui-space-24;
  font-size: $ui-font-size-metadata;
}
.access-summary {
  border-left: 1px solid $ui-line;
  padding-left: $ui-space-32;
}
.summary-module {
  display: flex;
  justify-content: space-between;
  gap: $ui-space-12;
  padding: $ui-space-8 0;
  border-bottom: 1px solid $ui-line;
}
.summary-help {
  margin-top: $ui-space-16;
  font-size: $ui-font-size-metadata;
}
.assignment-footer,
.editor-bottom {
  display: flex;
  align-items: center;
  gap: $ui-space-12;
  flex-wrap: wrap;
  padding-top: $ui-space-20;
  margin-top: $ui-space-20;
  border-top: 1px solid $ui-line;
}
.assignment-footer > span {
  margin-right: auto;
}
.editor-bottom > span {
  margin-right: auto;
}
.history-entry {
  padding: $ui-space-20 0;
  border-top: 1px solid $ui-line;
}
.history-entry summary {
  color: $ui-blue;
  cursor: pointer;
  margin-top: $ui-space-12;
}
.history-entry details {
  overflow-wrap: anywhere;
}
.skeleton-block span {
  display: block;
  height: 40px;
  margin: $ui-space-16 0;
  background: $ui-surface-soft;
  border-radius: $ui-control-radius;
}
.access-dialog {
  color: $ui-text;
  font-family: $ui-font-body;
  font-size: $ui-font-size-body;
  line-height: 1.5;
}
.dialog-body {
  padding: $ui-space-24;
}
.diff-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: $ui-space-24;
  margin-top: $ui-space-24;
}
.diff-grid li {
  padding: $ui-space-4 0;
}
.role-review {
  margin-top: $ui-space-20;
}
.dialog-footer {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: $ui-space-12;
  padding: $ui-space-20 $ui-space-24;
  border-top: 1px solid $ui-line;
}
button:focus-visible,
summary:focus-visible {
  outline: 2px solid $ui-blue;
  outline-offset: 3px;
}
@media (max-width: 1180px) {
  .details-grid {
    grid-template-columns: 1fr;
    gap: 0;
  }
}
@media (max-width: 1100px) {
  .user-access-grid { grid-template-columns: 1fr; }
  .user-access-list { max-height: 460px; padding-right: 0; overflow-y: auto; }
  .user-access-detail { padding-left: 0; border-left: 0; border-top: 1px solid $ui-line; }
}
@media (max-width: 760px) {
  .member-columns { grid-template-columns: 1fr; }
  .member-column + .member-column { border-left: 0; padding-left: 0; border-top: 1px solid $ui-line; padding-top: $ui-space-20; }
  .member-list { max-height: 36vh; }
  .role-overview-grid {
    grid-template-columns: 1fr;
  }
  .assignment-grid,
  .diff-grid {
    grid-template-columns: 1fr;
  }
  .access-summary {
    border-left: 0;
    padding: $ui-space-20 0 0;
    border-top: 1px solid $ui-line;
  }
  .permissions-page {
    padding: $ui-space-16 0 $ui-space-48;
  }
}
</style>

<style scoped lang="scss">
@media (max-width: 600px) { .module-count { display: none; } }
</style>
